import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  TextField,
  Stack,
  Divider,
  Alert,
  CircularProgress,
  Chip,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
  Collapse,
  IconButton,
  Tooltip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Card,
  CardContent,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Badge,
  Avatar
} from '@mui/material';
import GavelIcon from '@mui/icons-material/Gavel';
import MonetizationOnIcon from '@mui/icons-material/MonetizationOn';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import PersonIcon from '@mui/icons-material/Person';
import HomeIcon from '@mui/icons-material/Home';
import AddIcon from '@mui/icons-material/Add';
import HistoryIcon from '@mui/icons-material/History';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import HandshakeIcon from '@mui/icons-material/Handshake';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import PendingActionsIcon from '@mui/icons-material/PendingActions';
import TaskAltIcon from '@mui/icons-material/TaskAlt';
import RefreshIcon from '@mui/icons-material/Refresh';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { useTranslation } from 'react-i18next';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import penaltyAdminService from '../../services/penaltyAdminService';

/**
 * Penalty types available for selection
 */
const PENALTY_TYPES = [
  { value: 'admin_decision', label: 'Décision administrative', description: 'Sanction suite à une décision de l\'admin' },
  { value: 'no_show', label: 'Non-présentation', description: 'L\'utilisateur ne s\'est pas présenté' },
  { value: 'late_return', label: 'Retour en retard', description: 'Retour de l\'article après la date prévue' },
  { value: 'damage', label: 'Dommage', description: 'Dommage causé à un article' },
  { value: 'policy_violation', label: 'Violation des règles', description: 'Non-respect des conditions d\'utilisation' },
  { value: 'repeated_offense', label: 'Récidive', description: 'Infraction répétée' }
];

/**
 * Supported currencies
 */
const CURRENCIES = [
  { value: 'EUR', label: 'Euro (€)', symbol: '€' },
  { value: 'DZD', label: 'Dinar algérien (DZD)', symbol: 'DZD' },
  { value: 'USD', label: 'Dollar US ($)', symbol: '$' }
];

/**
 * Get avatar color from user name
 */
const getAvatarColor = (name) => {
  const colors = ['primary', 'secondary', 'error', 'warning', 'info', 'success'];
  if (!name) return colors[0];
  const charCode = name.charCodeAt(0);
  return colors[charCode % colors.length];
};

/**
 * Get initials from name
 */
const getInitials = (name) => {
  if (!name) return '?';
  return name
    .split(' ')
    .map(part => part[0])
    .join('')
    .toUpperCase()
    .substring(0, 2);
};

/**
 * Get profile picture URL with cache-buster
 */
const getProfilePictureUrl = (user) => {
  if (!user?.profilePicture) return '';

  const url = String(user.profilePicture);
  const simpleHash = url.split('').reduce((acc, char) => {
    return ((acc << 5) - acc) + char.charCodeAt(0) | 0;
  }, 0);

  const sep = url.includes('?') ? '&' : '?';
  return `${url}${sep}v=${Math.abs(simpleHash).toString(36)}`;
};

/**
 * Get status color and icon
 */
const getStatusInfo = (status) => {
  const statusMap = {
    pending: { color: 'warning', icon: <PendingActionsIcon />, label: 'En attente' },
    pending_collection: { color: 'warning', icon: <PendingActionsIcon />, label: 'Recouvrement en cours' },
    scheduled: { color: 'info', icon: <HistoryIcon />, label: 'Programmée' },
    applied: { color: 'success', icon: <CheckCircleIcon />, label: 'Appliquée' },
    failed: { color: 'error', icon: <ErrorOutlineIcon />, label: 'Échec' },
    waived: { color: 'default', icon: <HandshakeIcon />, label: 'Annulée' },
    disputed: { color: 'secondary', icon: <GavelIcon />, label: 'Contestée' }
  };
  return statusMap[status] || { color: 'default', icon: <InfoOutlinedIcon />, label: status };
};

/**
 * Calcule les totaux de pénalités existantes par direction (pour ou contre le renter)
 * @param {Array} penalties - Tableau de pénalités existantes
 * @param {Object} parties - Objet contenant { owner, renter }
 * @returns {Object} { totalAgainstRenter, totalForRenter }
 */
const calculatePenaltyTotals = (penalties, parties) => {
  if (!parties || !penalties || penalties.length === 0) {
    return {
      totalAgainstRenter: 0,  // Quand renter est sanctionné (EN DÉFAVEUR du renter)
      totalForRenter: 0       // Quand owner est sanctionné (EN FAVEUR du renter)
    };
  }

  return penalties.reduce((acc, penalty) => {
    // Si c'est le renter qui est sanctionné → EN DÉFAVEUR du renter
    if (penalty.user_id === parties.renter?.id) {
      acc.totalAgainstRenter += parseFloat(penalty.amount) || 0;
    }
    // Si c'est l'owner qui est sanctionné → EN FAVEUR du renter
    else if (penalty.user_id === parties.owner?.id) {
      acc.totalForRenter += parseFloat(penalty.amount) || 0;
    }
    return acc;
  }, { totalAgainstRenter: 0, totalForRenter: 0 });
};

/**
 * Get victim compensation status info
 */
const getVictimCompensationInfo = (victimComp) => {
  if (!victimComp || victimComp.status === 'not_required') {
    return { color: 'default', label: 'Non requise', icon: <InfoOutlinedIcon /> };
  }
  if (victimComp.status === 'transferred' || victimComp.status === 'compensated') {
    return { color: 'success', label: 'Indemnisée', icon: <CheckCircleIcon /> };
  }
  if (victimComp.status === 'requires_manual_action') {
    return { color: 'warning', label: 'Action manuelle requise', icon: <WarningAmberIcon /> };
  }
  return { color: 'info', label: 'En attente', icon: <PendingActionsIcon /> };
};

/**
 * PenaltyCard - Affiche une pénalité existante en format dossier structuré
 */
const PenaltyCard = ({ penalty, onMarkCompensated, onRefresh, expanded, onToggle, reservation }) => {
  const execMeta = penalty.execution_metadata || {};
  const victimComp = execMeta.victim_compensation || {};
  const statusInfo = getStatusInfo(penalty.status);
  const victimInfo = getVictimCompensationInfo(victimComp);

  // Résoudre l'utilisateur sanctionné depuis penalty.user ou depuis reservation
  const sanctionedUserFromReservation = penalty.user_id === reservation?.renter_id || penalty.user_id === reservation?.renter?.id
    ? reservation?.renter
    : penalty.user_id === reservation?.listing?.ownerId || penalty.user_id === reservation?.listing?.owner_id
      ? reservation?.listing?.owner
      : null;

  const sanctionedUser = {
    ...(penalty.user || {}),
    ...(sanctionedUserFromReservation || {})
  };

  // Résoudre l'utilisateur bénéficiaire depuis reservation
  const victimUserId = victimComp.victim_user_id;
  const victimUserFromReservation = victimUserId && reservation
    ? (victimUserId === reservation?.renter_id || victimUserId === reservation?.renter?.id
        ? reservation?.renter
        : victimUserId === reservation?.listing?.ownerId || victimUserId === reservation?.listing?.owner_id
          ? reservation?.listing?.owner
          : null)
    : null;

  const victimUser = {
    ...(victimComp.victim_user || {}),
    ...(victimUserFromReservation || {})
  };
  
  const [compensateDialogOpen, setCompensateDialogOpen] = useState(false);
  const [compensationMethod, setCompensationMethod] = useState('virement_bancaire');
  const [compensationReference, setCompensationReference] = useState('');
  const [compensationNotes, setCompensationNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const handleMarkCompensated = async () => {
    setSubmitting(true);
    try {
      await onMarkCompensated(penalty.id, {
        compensation_method: compensationMethod,
        compensation_reference: compensationReference,
        notes: compensationNotes
      });
      setCompensateDialogOpen(false);
      setCompensationMethod('virement_bancaire');
      setCompensationReference('');
      setCompensationNotes('');
    } finally {
      setSubmitting(false);
    }
  };

  const needsManualAction = victimComp.status === 'requires_manual_action';
  const isFullyComplete = penalty.status === 'applied' && 
    (victimComp.status === 'transferred' || victimComp.status === 'compensated' || victimComp.status === 'not_required');

  return (
    <>
      <Accordion 
        expanded={expanded} 
        onChange={onToggle}
        sx={{ 
          mb: 1, 
          border: '1px solid',
          borderColor: needsManualAction ? 'warning.main' : (isFullyComplete ? 'success.light' : 'divider'),
          borderRadius: 1,
          '&:before': { display: 'none' },
          overflow: 'hidden'
        }}
      >
        <AccordionSummary 
          expandIcon={<ExpandMoreIcon />}
          sx={{ 
            bgcolor: needsManualAction ? 'warning.50' : (isFullyComplete ? 'success.50' : 'grey.50'),
            '&:hover': { bgcolor: needsManualAction ? 'warning.100' : (isFullyComplete ? 'success.100' : 'grey.100') }
          }}
        >
          <Stack direction="row" spacing={2} alignItems="center" sx={{ width: '100%', pr: 2 }}>
            <Badge 
              badgeContent={needsManualAction ? '!' : null} 
              color="warning"
              invisible={!needsManualAction}
            >
              <MonetizationOnIcon color={statusInfo.color} />
            </Badge>
            <Box sx={{ flex: 1 }}>
              <Typography variant="subtitle1" fontWeight="bold">
                {parseFloat(penalty.amount).toFixed(2)} {penalty.currency}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {penalty.reason?.substring(0, 60)}{penalty.reason?.length > 60 ? '...' : ''}
              </Typography>
            </Box>
            <Chip 
              icon={statusInfo.icon}
              label={statusInfo.label}
              color={statusInfo.color}
              size="small"
              variant="filled"
            />
            <Typography variant="caption" color="text.secondary" sx={{ minWidth: 100, textAlign: 'right' }}>
              {penalty.created_at ? format(new Date(penalty.created_at), 'dd MMM yyyy', { locale: fr }) : ''}
            </Typography>
          </Stack>
        </AccordionSummary>
        
        <AccordionDetails sx={{ p: 0 }}>
          {/* Section 0: Parties impliquées */}
          <Box sx={{ p: 2, bgcolor: 'primary.50', borderBottom: '2px solid', borderColor: 'primary.light' }}>
            <Typography variant="overline" color="text.secondary" gutterBottom display="block" sx={{ mb: 1.5 }}>
              👥 Parties impliquées
            </Typography>
            <Stack direction="row" spacing={2} alignItems="center" justifyContent="space-between" sx={{ flexWrap: 'wrap' }}>
              {/* Sanctionné */}
              <Tooltip title={`Utilisateur sanctionné (ID: ${penalty.user_id?.substring(0, 8)})`}>
                <Stack direction="row" spacing={1} alignItems="center" sx={{ flex: 1, minWidth: 150 }}>
                  <Avatar
                    src={getProfilePictureUrl(sanctionedUser) || undefined}
                    sx={{
                      bgcolor: 'primary.main',
                      width: 40,
                      height: 40,
                      fontWeight: 'bold'
                    }}
                  >
                    {sanctionedUser?.name ? getInitials(sanctionedUser.name) : <PersonIcon />}
                  </Avatar>
                  <Stack spacing={0}>
                    <Typography variant="body2" fontWeight="bold" sx={{ wordBreak: 'break-word' }}>
                      {sanctionedUser?.name || sanctionedUser?.email || 'Utilisateur'}
                    </Typography>
                    <Chip label="Sanctionné" size="small" color="error" variant="outlined" sx={{ width: 'fit-content' }} />
                  </Stack>
                </Stack>
              </Tooltip>

              {/* Flèche */}
              <ArrowForwardIcon sx={{ color: 'text.secondary', fontSize: 24 }} />

              {/* Bénéficiaire */}
              {penalty.execution_metadata?.victim_compensation?.victim_user_id && (
                <Tooltip title={`Bénéficiaire indemnisé (ID: ${penalty.execution_metadata.victim_compensation.victim_user_id?.substring(0, 8)})`}>
                  <Stack direction="row" spacing={1} alignItems="center" sx={{ flex: 1, minWidth: 150 }}>
                    <Avatar
                      src={getProfilePictureUrl(victimUser) || undefined}
                      sx={{
                        bgcolor: 'success.main',
                        width: 40,
                        height: 40,
                        fontWeight: 'bold'
                      }}
                    >
                      {victimUser?.name ? getInitials(victimUser.name) : <PersonIcon />}
                    </Avatar>
                    <Stack spacing={0}>
                      <Typography variant="body2" fontWeight="bold" sx={{ wordBreak: 'break-word' }}>
                        {victimUser?.name || victimUser?.email || 'Victime'}
                      </Typography>
                      <Chip label="Bénéficiaire" size="small" color="success" variant="outlined" sx={{ width: 'fit-content' }} />
                    </Stack>
                  </Stack>
                </Tooltip>
              )}
            </Stack>
          </Box>

          {/* Section 1: Informations générales */}
          <Box sx={{ p: 2, bgcolor: 'background.paper' }}>
            <Typography variant="overline" color="text.secondary" gutterBottom display="block">
              📋 Informations de la sanction
            </Typography>
            <Stack spacing={1}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography variant="body2" color="text.secondary">Montant sanctionné</Typography>
                <Typography variant="body2" fontWeight="bold">{parseFloat(penalty.amount).toFixed(2)} {penalty.currency}</Typography>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography variant="body2" color="text.secondary">Type</Typography>
                <Chip label={PENALTY_TYPES.find(p => p.value === penalty.penalty_type)?.label || penalty.penalty_type} size="small" variant="outlined" />
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography variant="body2" color="text.secondary">Statut</Typography>
                <Chip icon={statusInfo.icon} label={statusInfo.label} color={statusInfo.color} size="small" />
              </Box>
              {penalty.applied_at && (
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="body2" color="text.secondary">Appliquée le</Typography>
                  <Typography variant="body2">{format(new Date(penalty.applied_at), 'dd MMM yyyy à HH:mm', { locale: fr })}</Typography>
                </Box>
              )}
            </Stack>
          </Box>

          <Divider />

          {/* Section 2: Détails du recouvrement */}
          {execMeta.collection_details && execMeta.collection_details.length > 0 && (
            <>
              <Box sx={{ p: 2, bgcolor: 'success.50' }}>
                <Typography variant="overline" color="text.secondary" gutterBottom display="block">
                  💰 Recouvrement effectué
                </Typography>
                <Stack spacing={1.5}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="body2" color="text.secondary">Total recouvré</Typography>
                    <Chip 
                      icon={<CheckCircleIcon />}
                      label={`${execMeta.total_collected?.toFixed(2)} ${execMeta.collection_currency || execMeta.collection_details?.[0]?.currency || 'EUR'}`}
                      color="success"
                      size="small"
                    />
                  </Box>
                  
                  <Typography variant="caption" color="text.secondary" fontWeight="bold" sx={{ mt: 1 }}>
                    Sources de recouvrement :
                  </Typography>
                  {execMeta.collection_details.map((detail, idx) => (
                    <Card key={idx} variant="outlined" sx={{ bgcolor: 'background.paper' }}>
                      <CardContent sx={{ py: 1, px: 2, '&:last-child': { pb: 1 } }}>
                        <Stack direction="row" justifyContent="space-between" alignItems="center">
                          <Stack direction="row" spacing={1} alignItems="center">
                            {detail.source === 'deposit' ? (
                              <AccountBalanceWalletIcon fontSize="small" color="primary" />
                            ) : (
                              <ReceiptLongIcon fontSize="small" color="secondary" />
                            )}
                            <Box>
                              <Typography variant="body2" fontWeight="medium">
                                {detail.source === 'deposit' ? 'Caution' : 'Paiement en attente'}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                {detail.note || (detail.source === 'deposit' ? 'Prélèvement sur caution' : 'Déduction sur payout')}
                              </Typography>
                            </Box>
                          </Stack>
                          <Typography variant="body2" fontWeight="bold" color="success.main">
                            +{(detail.amount || detail.deducted_amount)?.toFixed(2)} {detail.currency}
                          </Typography>
                        </Stack>
                      </CardContent>
                    </Card>
                  ))}
                </Stack>
              </Box>
              <Divider />
            </>
          )}

          {/* Section 3: Indemnisation victime */}
          {victimComp.victim_user_id && (
            <Box sx={{ p: 2, bgcolor: needsManualAction ? 'warning.50' : (victimInfo.color === 'success' ? 'success.50' : 'grey.50') }}>
              <Typography variant="overline" color="text.secondary" gutterBottom display="block">
                🤝 Indemnisation de la victime
              </Typography>
              <Stack spacing={1.5}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography variant="body2" color="text.secondary">Statut</Typography>
                  <Chip 
                    icon={victimInfo.icon}
                    label={victimInfo.label}
                    color={victimInfo.color}
                    size="small"
                  />
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="body2" color="text.secondary">Montant à verser</Typography>
                  <Typography variant="body2" fontWeight="bold">{victimComp.amount_to_compensate?.toFixed(2)} {victimComp.currency}</Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="body2" color="text.secondary">Méthode</Typography>
                  <Typography variant="body2">{victimComp.compensation_method || 'Non définie'}</Typography>
                </Box>
                
                {victimComp.note && (
                  <Alert 
                    severity={needsManualAction ? 'warning' : 'info'} 
                    sx={{ mt: 1 }}
                    icon={needsManualAction ? <WarningAmberIcon /> : <InfoOutlinedIcon />}
                  >
                    <Typography variant="caption">{victimComp.note}</Typography>
                  </Alert>
                )}

                {victimComp.transfer_blocked_reason && (
                  <Alert severity="warning" sx={{ mt: 1 }}>
                    <Typography variant="caption" fontWeight="bold">Raison du blocage :</Typography>
                    <Typography variant="caption" display="block">{victimComp.transfer_blocked_reason}</Typography>
                  </Alert>
                )}

                {victimComp.compensated_at && (
                  <Alert severity="success" sx={{ mt: 1 }} icon={<CheckCircleIcon />}>
                    <Typography variant="caption">
                      Compensation confirmée le {format(new Date(victimComp.compensated_at), 'dd MMM yyyy à HH:mm', { locale: fr })}
                      {victimComp.compensation_reference && ` (Réf: ${victimComp.compensation_reference})`}
                    </Typography>
                  </Alert>
                )}

                {/* Bouton action manuelle */}
                {needsManualAction && (
                  <Box sx={{ mt: 2, pt: 2, borderTop: '1px dashed', borderColor: 'warning.main' }}>
                    <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                      ⚠️ Cette indemnisation nécessite une action manuelle de votre part.
                    </Typography>
                    <Button
                      variant="contained"
                      color="success"
                      size="small"
                      startIcon={<TaskAltIcon />}
                      onClick={() => setCompensateDialogOpen(true)}
                    >
                      Confirmer l'indemnisation
                    </Button>
                  </Box>
                )}
              </Stack>
            </Box>
          )}

          {/* Section 4: Opérations Stripe */}
          {execMeta.stripe_operations && execMeta.stripe_operations.length > 0 && (
            <>
              <Divider />
              <Box sx={{ p: 2, bgcolor: 'info.50' }}>
                <Typography variant="overline" color="text.secondary" gutterBottom display="block" sx={{ mb: 1 }}>
                  💳 Détails des opérations Stripe ({execMeta.stripe_operations.length})
                </Typography>
                <Stack spacing={1.5}>
                  {execMeta.stripe_operations.map((op, idx) => (
                    <Card key={idx} variant="outlined" sx={{ bgcolor: 'background.paper', borderLeft: 3, borderLeftColor: op.status === 'succeeded' || op.type === 'victim_compensation_transfer' ? 'success.main' : (op.error ? 'error.main' : 'info.main') }}>
                      <CardContent sx={{ py: 1.5, px: 2, '&:last-child': { pb: 1.5 } }}>
                        <Stack spacing={1}>
                          {/* Type et statut */}
                          <Stack direction="row" justifyContent="space-between" alignItems="center">
                            <Chip 
                              label={op.type === 'payout_reduction' ? 'Réduction de payout' : op.type === 'deposit_capture' ? 'Capture de caution' : op.type === 'victim_compensation_transfer' ? 'Transfert victime' : op.type} 
                              size="small" 
                              color={op.status === 'succeeded' || op.type === 'victim_compensation_transfer' ? 'success' : (op.error ? 'error' : 'info')}
                              variant="filled"
                            />
                            {op.amount && (
                              <Typography variant="body2" fontWeight="bold" color={op.status === 'succeeded' || op.type === 'victim_compensation_transfer' ? 'success.main' : 'text.primary'}>
                                {op.amount?.toFixed(2)} {op.currency || penalty.currency}
                              </Typography>
                            )}
                          </Stack>

                          {/* IDs Stripe */}
                          {op.payout_id && (
                            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                              <Typography variant="caption" color="text.secondary" sx={{ minWidth: 80 }}>
                                Payout ID:
                              </Typography>
                              <Typography 
                                variant="caption" 
                                sx={{ 
                                  fontFamily: 'monospace', 
                                  bgcolor: 'grey.100', 
                                  px: 1, 
                                  py: 0.5, 
                                  borderRadius: 0.5,
                                  flex: 1,
                                  fontSize: '0.7rem'
                                }}
                              >
                                {op.payout_id}
                              </Typography>
                            </Box>
                          )}

                          {op.transfer_id && (
                            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                              <Typography variant="caption" color="text.secondary" sx={{ minWidth: 80 }}>
                                Transfer ID:
                              </Typography>
                              <Typography 
                                variant="caption" 
                                sx={{ 
                                  fontFamily: 'monospace', 
                                  bgcolor: 'grey.100', 
                                  px: 1, 
                                  py: 0.5, 
                                  borderRadius: 0.5,
                                  flex: 1,
                                  fontSize: '0.7rem'
                                }}
                              >
                                {op.transfer_id}
                              </Typography>
                            </Box>
                          )}

                          {op.payment_intent_id && (
                            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                              <Typography variant="caption" color="text.secondary" sx={{ minWidth: 80 }}>
                                Payment ID:
                              </Typography>
                              <Typography 
                                variant="caption" 
                                sx={{ 
                                  fontFamily: 'monospace', 
                                  bgcolor: 'grey.100', 
                                  px: 1, 
                                  py: 0.5, 
                                  borderRadius: 0.5,
                                  flex: 1,
                                  fontSize: '0.7rem'
                                }}
                              >
                                {op.payment_intent_id}
                              </Typography>
                            </Box>
                          )}

                          {/* Erreur si présente */}
                          {op.error && (
                            <Alert severity="error" sx={{ py: 0 }}>
                              <Typography variant="caption">{op.error}</Typography>
                            </Alert>
                          )}

                          {/* Note si présente */}
                          {op.note && !op.error && (
                            <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                              {op.note}
                            </Typography>
                          )}
                        </Stack>
                      </CardContent>
                    </Card>
                  ))}

                  {/* Résumé des opérations */}
                  <Box sx={{ mt: 1, pt: 1, borderTop: '1px dashed', borderColor: 'divider' }}>
                    <Stack direction="row" justifyContent="space-between" alignItems="center">
                      <Typography variant="caption" color="text.secondary">
                        Total collecté via Stripe
                      </Typography>
                      <Typography variant="caption" fontWeight="bold" color="success.main">
                        {execMeta.total_collected?.toFixed(2)} {execMeta.collection_currency || execMeta.collection_details?.[0]?.currency || 'EUR'}
                      </Typography>
                    </Stack>
                    {execMeta.amount_remaining > 0 && (
                      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mt: 0.5 }}>
                        <Typography variant="caption" color="text.secondary">
                          Montant restant non collecté (équiv. en {penalty.currency})
                        </Typography>
                        <Typography variant="caption" fontWeight="bold" color="warning.main">
                          {execMeta.amount_remaining?.toFixed(2)} €
                        </Typography>
                      </Stack>
                    )}
                  </Box>
                </Stack>
              </Box>
            </>
          )}
        </AccordionDetails>
      </Accordion>

      {/* Dialog pour confirmer l'indemnisation manuelle */}
      <Dialog open={compensateDialogOpen} onClose={() => setCompensateDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Stack direction="row" spacing={1} alignItems="center">
            <TaskAltIcon color="success" />
            <Typography variant="h6">Confirmer l'indemnisation manuelle</Typography>
          </Stack>
        </DialogTitle>
        <DialogContent>
          <Alert severity="info" sx={{ mb: 2 }}>
            Confirmez que vous avez effectué le versement de <strong>{victimComp.amount_to_compensate?.toFixed(2)} {victimComp.currency}</strong> à la victime par un autre canal.
          </Alert>
          
          <Stack spacing={2} sx={{ mt: 2 }}>
            <FormControl fullWidth size="small">
              <InputLabel>Méthode de compensation</InputLabel>
              <Select
                value={compensationMethod}
                onChange={(e) => setCompensationMethod(e.target.value)}
                label="Méthode de compensation"
              >
                <MenuItem value="virement_bancaire">Virement bancaire</MenuItem>
                <MenuItem value="cheque">Chèque</MenuItem>
                <MenuItem value="especes">Espèces</MenuItem>
                <MenuItem value="paypal">PayPal</MenuItem>
                <MenuItem value="credit_plateforme">Crédit plateforme</MenuItem>
                <MenuItem value="autre">Autre</MenuItem>
              </Select>
            </FormControl>

            <TextField
              label="Référence du paiement"
              value={compensationReference}
              onChange={(e) => setCompensationReference(e.target.value)}
              fullWidth
              size="small"
              placeholder="Ex: VIR-2024-12345"
              helperText="Numéro de virement, référence chèque, etc."
            />

            <TextField
              label="Notes internes"
              value={compensationNotes}
              onChange={(e) => setCompensationNotes(e.target.value)}
              fullWidth
              size="small"
              multiline
              rows={2}
              placeholder="Notes additionnelles..."
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCompensateDialogOpen(false)} disabled={submitting}>
            Annuler
          </Button>
          <Button
            variant="contained"
            color="success"
            onClick={handleMarkCompensated}
            disabled={submitting}
            startIcon={submitting ? <CircularProgress size={16} /> : <CheckCircleIcon />}
          >
            Confirmer l'indemnisation
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

/**
 * MonetaryPenaltyForm - Composant réutilisable pour appliquer une sanction monétaire
 * Affichage structuré en dossiers avec accordéon et pagination infinie
 */
const MonetaryPenaltyForm = ({
  sourceType = 'manual',
  sourceId = null,
  parties = null,
  targetUserId = '',
  targetUserName = '',
  reservationId = null,
  reservation = null,
  defaultCurrency = 'EUR',
  suggestedAmount = '',
  suggestedReason = '',
  onPenaltyCreated,
  onPenaltyApplied,
  onCancel,
  disabled = false,
  autoApply = false,
  compact = false,
  title
}) => {
  const { t } = useTranslation('penalties');

  // Form state
  const [selectedParty, setSelectedParty] = useState('');
  const [userId, setUserId] = useState(targetUserId);
  const [penaltyType, setPenaltyType] = useState('admin_decision');
  const [amount, setAmount] = useState(suggestedAmount);
  const [currency, setCurrency] = useState(defaultCurrency);
  const [reason, setReason] = useState(suggestedReason);
  const [internalNote, setInternalNote] = useState('');
  
  // UI state
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [showNewPenaltyForm, setShowNewPenaltyForm] = useState(false);
  const [existingPenalties, setExistingPenalties] = useState([]);
  const [loadingExisting, setLoadingExisting] = useState(false);
  const [expandedPenaltyId, setExpandedPenaltyId] = useState(null);
  const [displayCount, setDisplayCount] = useState(3);

  // Derived
  const sanctionedParty = selectedParty ? parties?.[selectedParty] : null;
  const victimParty = selectedParty === 'owner' ? parties?.renter : (selectedParty === 'renter' ? parties?.owner : null);

  // Calcul du montant maximum selon la partie sanctionnée
  const maxAmount = (() => {
    if (!selectedParty || !reservation) return null;
    
    if (selectedParty === 'renter') {
      // Renter sanctionné → limite = montant de la caution
      const depositAmount = reservation.deposit?.amount || reservation.deposit_amount;
      return depositAmount ? parseFloat(depositAmount) : null;
    } else if (selectedParty === 'owner') {
      // Owner sanctionné → limite = montant total payé pour la location (hors caution)
      const rentalPayments = reservation.payments || [];
      const totalRentalPaid = rentalPayments
        .filter(p => p.payment_type === 'rental')
        .reduce((sum, p) => sum + parseFloat(p.amount || 0), 0);
      return totalRentalPaid > 0 ? totalRentalPaid : (reservation.total_amount ? parseFloat(reservation.total_amount) : null);
    }
    return null;
  })();

  const amountExceedsMax = maxAmount !== null && amount && parseFloat(amount) > maxAmount;

  // Load existing penalties
  const loadExistingPenalties = useCallback(async () => {
    if (!sourceType || !sourceId) return;
    
    setLoadingExisting(true);
    try {
      const result = await penaltyAdminService.getBySource(sourceType, sourceId);
      if (result?.data) {
        const penalties = Array.isArray(result.data) ? result.data : [result.data];
        setExistingPenalties(penalties);
        
        const needsAction = penalties.find(p => 
          p.execution_metadata?.victim_compensation?.status === 'requires_manual_action'
        );
        if (needsAction) {
          setExpandedPenaltyId(needsAction.id);
        } else if (penalties.length > 0) {
          setExpandedPenaltyId(penalties[0].id);
        }
      }
    } catch (err) {
      console.error('Failed to load existing penalties:', err);
    } finally {
      setLoadingExisting(false);
    }
  }, [sourceType, sourceId]);

  useEffect(() => {
    loadExistingPenalties();
  }, [loadExistingPenalties]);

  useEffect(() => {
    if (targetUserId && !parties) setUserId(targetUserId);
  }, [targetUserId, parties]);

  useEffect(() => {
    if (selectedParty && parties?.[selectedParty]) {
      setUserId(parties[selectedParty].id);
    }
  }, [selectedParty, parties]);

  useEffect(() => {
    if (suggestedAmount) setAmount(suggestedAmount);
  }, [suggestedAmount]);

  useEffect(() => {
    if (suggestedReason) setReason(suggestedReason);
  }, [suggestedReason]);

  const validateForm = () => {
    if (parties && !selectedParty) {
      setError('Veuillez sélectionner la partie à sanctionner');
      return false;
    }
    if (!userId) {
      setError('L\'ID de l\'utilisateur est requis');
      return false;
    }
    if (!amount || parseFloat(amount) <= 0) {
      setError('Le montant doit être supérieur à 0');
      return false;
    }
    if (amountExceedsMax) {
      if (selectedParty === 'renter') {
        setError(`Le montant ne peut pas dépasser la caution (${maxAmount} ${currency})`);
      } else {
        setError(`Le montant ne peut pas dépasser le paiement de location (${maxAmount} ${currency})`);
      }
      return false;
    }
    if (!reason.trim()) {
      setError('La raison est requise');
      return false;
    }
    return true;
  };

  const handleCreateAndApply = async () => {
    if (!validateForm()) return;

    setLoading(true);
    setError(null);
    setSuccess(null);

    try {
      const payload = {
        user_id: userId,
        penalty_type: penaltyType,
        amount: parseFloat(amount),
        currency,
        reason: reason.trim(),
        source_type: sourceType,
        source_id: sourceId,
        reservation_id: reservationId,
        status: 'pending',
        event_message: internalNote || `Pénalité créée et appliquée depuis ${sourceType}`
      };

      const createResult = await penaltyAdminService.create(payload);
      onPenaltyCreated?.(createResult.data);

      const applyResult = await penaltyAdminService.apply(createResult.data.id, {
        victim_user_id: victimParty?.id || null,
        victim_has_stripe: victimParty?.hasStripe ?? false
      });

      setSuccess('Pénalité créée et appliquée avec succès');
      onPenaltyApplied?.(applyResult.data);
      
      setShowNewPenaltyForm(false);
      setAmount('');
      setReason(suggestedReason);
      setSelectedParty('');
      await loadExistingPenalties();
      
    } catch (err) {
      console.error('Failed to create and apply penalty:', err);
      setError(err.response?.data?.message || err.message || 'Échec de l\'opération');
    } finally {
      setLoading(false);
    }
  };

  const handleMarkCompensated = async (penaltyId, options) => {
    try {
      await penaltyAdminService.markVictimCompensated(penaltyId, options);
      setSuccess('Indemnisation confirmée avec succès');
      await loadExistingPenalties();
    } catch (err) {
      console.error('Failed to mark compensated:', err);
      setError(err.response?.data?.message || err.message || 'Échec de la confirmation');
    }
  };

  const displayedPenalties = existingPenalties.slice(0, displayCount);
  const hasMorePenalties = existingPenalties.length > displayCount;
  const pendingActionCount = existingPenalties.filter(p => 
    p.execution_metadata?.victim_compensation?.status === 'requires_manual_action'
  ).length;

  const currencySymbol = CURRENCIES.find(c => c.value === currency)?.symbol || currency;

  return (
    <Paper 
      elevation={compact ? 1 : 2} 
      sx={{ 
        p: compact ? 2 : 3, 
        mb: 2, 
        bgcolor: 'background.paper',
        border: '2px solid',
        borderColor: pendingActionCount > 0 ? 'warning.main' : 'primary.main',
        borderRadius: 2
      }}
    >
      {/* Header */}
      <Stack direction="row" spacing={2} alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
        <Stack direction="row" spacing={1} alignItems="center">
          <GavelIcon color={pendingActionCount > 0 ? 'warning' : 'primary'} />
          <Typography variant="h6" fontWeight="bold">
            {title || 'Sanctions monétaires'}
          </Typography>
          {pendingActionCount > 0 && (
            <Chip 
              label={`${pendingActionCount} action${pendingActionCount > 1 ? 's' : ''} requise${pendingActionCount > 1 ? 's' : ''}`}
              color="warning"
              size="small"
            />
          )}
        </Stack>
        <Stack direction="row" spacing={1}>
          <Tooltip title="Rafraîchir">
            <IconButton size="small" onClick={loadExistingPenalties} disabled={loadingExisting}>
              <RefreshIcon />
            </IconButton>
          </Tooltip>
          {!showNewPenaltyForm && (
            <Button
              variant="contained"
              size="small"
              startIcon={<AddIcon />}
              onClick={() => setShowNewPenaltyForm(true)}
              disabled={disabled}
            >
              Nouvelle sanction
            </Button>
          )}
        </Stack>
      </Stack>

      {/* Messages */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      {/* Loading state */}
      {loadingExisting && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 2 }}>
          <CircularProgress size={20} />
          <Typography variant="body2" color="text.secondary">
            Chargement des sanctions...
          </Typography>
        </Box>
      )}

      {/* Existing penalties list */}
      {!loadingExisting && existingPenalties.length > 0 && (
        <Box sx={{ mb: showNewPenaltyForm ? 3 : 0 }}>
          <Typography variant="overline" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
            📂 Dossiers de sanctions ({existingPenalties.length})
          </Typography>
          
          {displayedPenalties.map((penalty) => (
            <PenaltyCard
              key={penalty.id}
              penalty={penalty}
              reservation={reservation}
              expanded={expandedPenaltyId === penalty.id}
              onToggle={() => setExpandedPenaltyId(expandedPenaltyId === penalty.id ? null : penalty.id)}
              onMarkCompensated={handleMarkCompensated}
              onRefresh={loadExistingPenalties}
            />
          ))}

          {hasMorePenalties && (
            <Button
              fullWidth
              variant="text"
              onClick={() => setDisplayCount(prev => prev + 5)}
              sx={{ mt: 1 }}
            >
              Afficher plus ({existingPenalties.length - displayCount} restantes)
            </Button>
          )}
        </Box>
      )}

      {/* Empty state */}
      {!loadingExisting && existingPenalties.length === 0 && !showNewPenaltyForm && (
        <Box sx={{ textAlign: 'center', py: 4 }}>
          <GavelIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
          <Typography variant="body2" color="text.secondary">
            Aucune sanction pour ce litige
          </Typography>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setShowNewPenaltyForm(true)}
            sx={{ mt: 2 }}
            disabled={disabled}
          >
            Créer une sanction
          </Button>
        </Box>
      )}

      {/* New Penalty Form */}
      <Collapse in={showNewPenaltyForm}>
        <Box sx={{ pt: 2, borderTop: existingPenalties.length > 0 ? '1px solid' : 'none', borderColor: 'divider' }}>
          <Typography variant="overline" color="text.secondary" sx={{ mb: 2, display: 'block' }}>
            ➕ Nouvelle sanction
          </Typography>

          <Stack spacing={2}>
            {/* Party selection */}
            {parties && (
              <FormControl fullWidth size="small" required>
                <InputLabel>Partie à sanctionner</InputLabel>
                <Select
                  value={selectedParty}
                  onChange={(e) => setSelectedParty(e.target.value)}
                  label="Partie à sanctionner"
                >
                  {parties.owner && (
                    <MenuItem value="owner">
                      <Stack direction="row" spacing={1} alignItems="center">
                        <HomeIcon fontSize="small" color="primary" />
                        <Typography variant="body2">
                          Propriétaire : <strong>{parties.owner.name || parties.owner.id?.substring(0, 8)}</strong>
                        </Typography>
                      </Stack>
                    </MenuItem>
                  )}
                  {parties.renter && (
                    <MenuItem value="renter">
                      <Stack direction="row" spacing={1} alignItems="center">
                        <PersonIcon fontSize="small" color="secondary" />
                        <Typography variant="body2">
                          Locataire : <strong>{parties.renter.name || parties.renter.id?.substring(0, 8)}</strong>
                        </Typography>
                      </Stack>
                    </MenuItem>
                  )}
                </Select>
                <FormHelperText>La victime sera automatiquement l'autre partie</FormHelperText>
              </FormControl>
            )}

            {selectedParty && victimParty && (
              <Alert severity="info" icon={<HandshakeIcon />}>
                <Typography variant="body2">
                  <strong>Sanctionné :</strong> {sanctionedParty?.name} | <strong>Victime :</strong> {victimParty?.name}
                  {!victimParty?.hasStripe && <Chip label="Pas de Stripe" size="small" color="warning" sx={{ ml: 1 }} />}
                </Typography>
              </Alert>
            )}

            {/* Affichage du montant maximum selon la partie sanctionnée */}
            {selectedParty && maxAmount !== null && (
              <Alert severity="info" sx={{ py: 0.5 }}>
                <Typography variant="body2">
                  {selectedParty === 'renter' ? (
                    <>💰 <strong>Montant maximum (caution) :</strong> {maxAmount.toFixed(2)} {currency}</>
                  ) : (
                    <>💳 <strong>Montant maximum (paiement location) :</strong> {maxAmount.toFixed(2)} {currency}</>
                  )}
                </Typography>
              </Alert>
            )}

            {/* Calcul et affichage des pénalités cumulées */}
            {selectedParty && (
              (() => {
                const totals = calculatePenaltyTotals(existingPenalties, parties);
                const cumulatedTotal = selectedParty === 'renter' ? totals.totalAgainstRenter : totals.totalForRenter;
                const newAmount = parseFloat(amount) || 0;
                const grandTotal = cumulatedTotal + newAmount;
                const willExceedMax = maxAmount !== null && grandTotal > maxAmount;

                return (
                  <>
                    {/* Affichage du breakdown cumulé */}
                    {cumulatedTotal > 0 && (
                      <Alert severity="warning" icon={<WarningAmberIcon />} sx={{ py: 1 }}>
                        <Stack spacing={0.5}>
                          <Typography variant="body2" fontWeight="bold">
                            {selectedParty === 'renter' 
                              ? '⚖️ Pénalités cumulées EN DÉFAVEUR du renter'
                              : '⚖️ Pénalités cumulées EN FAVEUR du renter'}
                          </Typography>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 0.5 }}>
                            <Typography variant="caption" color="inherit">
                              Historique :
                            </Typography>
                            <Typography variant="caption" fontWeight="bold" color="inherit">
                              {cumulatedTotal.toFixed(2)} {currency}
                            </Typography>
                          </Box>
                          {newAmount > 0 && (
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                              <Typography variant="caption" color="inherit">
                                + Nouveau montant :
                              </Typography>
                              <Typography variant="caption" fontWeight="bold" color="inherit">
                                {newAmount.toFixed(2)} {currency}
                              </Typography>
                            </Box>
                          )}
                          <Box sx={{ pt: 0.5, borderTop: '1px solid', borderColor: 'rgba(255,255,255,0.3)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <Typography variant="caption" fontWeight="bold" color="inherit">
                              TOTAL CUMULÉ :
                            </Typography>
                            <Typography variant="body2" fontWeight="bold" color="inherit">
                              {grandTotal.toFixed(2)} {currency}
                            </Typography>
                          </Box>
                        </Stack>
                      </Alert>
                    )}

                    {/* Alerte de dépassement (compatible avec le budget restant) */}
                    {willExceedMax && (
                      <Alert severity="error" icon={<WarningAmberIcon />} sx={{ py: 1 }}>
                        <Stack spacing={0.5}>
                          <Typography variant="body2" fontWeight="bold">
                            ⛔ ATTENTION : Dépassement de budget
                          </Typography>
                          <Typography variant="caption" color="inherit">
                            {selectedParty === 'renter'
                              ? `La somme des pénalités (${grandTotal.toFixed(2)} ${currency}) dépasse la caution disponible (${maxAmount.toFixed(2)} ${currency})`
                              : `La somme des pénalités (${grandTotal.toFixed(2)} ${currency}) dépasse le paiement de location (${maxAmount.toFixed(2)} ${currency})`}
                          </Typography>
                          <Typography variant="caption" color="inherit" sx={{ fontStyle: 'italic', mt: 0.5 }}>
                            Montant en excédent : {(grandTotal - maxAmount).toFixed(2)} {currency}
                          </Typography>
                        </Stack>
                      </Alert>
                    )}
                  </>
                );
              })()
            )}

            {/* Amount and currency */}
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <TextField
                label="Montant"
                type="number"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                fullWidth
                size="small"
                required
                error={amountExceedsMax}
                helperText={
                  amountExceedsMax
                    ? selectedParty === 'renter'
                      ? `Le montant ne peut pas dépasser la caution (${maxAmount.toFixed(2)} ${currency})`
                      : `Le montant ne peut pas dépasser le paiement de location (${maxAmount.toFixed(2)} ${currency})`
                    : ''
                }
                InputProps={{
                  startAdornment: <InputAdornment position="start">{currencySymbol}</InputAdornment>
                }}
                inputProps={{ 
                  min: 0, 
                  step: 0.01,
                  max: maxAmount || undefined
                }}
              />
              <FormControl sx={{ minWidth: 150 }} size="small" disabled>
                <InputLabel>Devise de la réservation</InputLabel>
                <Select 
                  value={currency} 
                  label="Devise"
                  disabled
                >
                  <MenuItem value={currency}>
                    {CURRENCIES.find(c => c.value === currency)?.label || currency}
                  </MenuItem>
                </Select>                
              </FormControl>
            </Stack>

            {/* Penalty type */}
            <FormControl fullWidth size="small">
              <InputLabel>Type de sanction</InputLabel>
              <Select value={penaltyType} onChange={(e) => setPenaltyType(e.target.value)} label="Type de sanction">
                {PENALTY_TYPES.map((type) => (
                  <MenuItem key={type.value} value={type.value}>{type.label}</MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* Reason */}
            <TextField
              label="Raison de la sanction"
              multiline
              rows={2}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              fullWidth
              required
              placeholder="Expliquez la raison de cette sanction..."
            />

            {/* Actions */}
            <Stack direction="row" spacing={2} justifyContent="flex-end" sx={{ mt: 2 }}>
              <Button variant="outlined" onClick={() => setShowNewPenaltyForm(false)} disabled={loading}>
                Annuler
              </Button>
              <Button
                variant="contained"
                color="warning"
                startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <MonetizationOnIcon />}
                onClick={handleCreateAndApply}
                disabled={loading || disabled || (parties && !selectedParty)}
              >
                {loading ? 'Traitement...' : 'Créer et appliquer'}
              </Button>
            </Stack>
          </Stack>
        </Box>
      </Collapse>

      {/* Info footer */}
      <Box sx={{ mt: 2, pt: 2, borderTop: '1px solid', borderColor: 'divider' }}>
        <Typography variant="caption" color="text.secondary">
          <InfoOutlinedIcon fontSize="small" sx={{ verticalAlign: 'middle', mr: 0.5 }} />
          Le recouvrement se fait en priorité sur la caution, puis par déduction sur les paiements. 
          Si le transfert automatique vers la victime échoue, une action manuelle sera requise.
        </Typography>
      </Box>
    </Paper>
  );
};

export default MonetaryPenaltyForm;
